import {
  AppCard,
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { TabContext } from "@mui/lab";
import { Box, Pagination, Tabs } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListServiceNegotiations } from "../../../../__generated__/orval/endpoints/service-negotiations/service-negotiations";
import type { ListServiceNegotiationsParams } from "../../../../__generated__/orval/models";
import { ListServiceNegotiations } from "../../../../components/molecules/ListServiceNegotiations";
import { SERVICE_NEGOTIATION_COMPLETED_STATUS_NAMES } from "../../../../constants/serviceNegotiationStatuses";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { ServiceNegotiationsFilters } from "./-internal_components/ServiceNegotiationsFilters";

const searchSchema = z.object({
  created_at: z.optional(z.string()).catch(undefined),
  identifier: z.optional(z.string()).catch(undefined),
  page: z.optional(z.coerce.number()).catch(undefined),
  reference_number: z.optional(z.string()).catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  service_quotation_id: z.optional(z.coerce.number()).catch(undefined),
  short_description: z.optional(z.string()).catch(undefined),
  status_name: z
    .optional(z.union([z.array(z.string()), z.string()]))
    .catch(undefined),
  tab: z.optional(z.string()).catch("all"),
});

type NegotiationsSearch = z.infer<typeof searchSchema>;

const parseSearchStrings = (
  value?: string | string[] | undefined,
): string[] | undefined => {
  if (value === undefined) return undefined;

  let items: string[];

  if (Array.isArray(value)) {
    items = value.map((v) => String(v));
  } else {
    items = String(value).split(",");
  }

  const parsed = items
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return parsed.length > 0 ? parsed : undefined;
};

const ServiceNegotiations: React.FC = () => {
  const { t } = useTranslation();
  const { tab = "all", page = 1, rowsPerPage = 25 } = Route.useSearch();

  const navigate = Route.useNavigate();
  const loaderDeps = Route.useLoaderDeps();
  const selectedTab = tab === "finished" ? "completed" : tab;

  const listParams: ListServiceNegotiationsParams = {
    created_at: loaderDeps.filters.created_at,
    identifier: loaderDeps.filters.identifier || undefined,
    items_per_page: loaderDeps.items_per_page,
    page: loaderDeps.page,
    service_quotation_id: loaderDeps.filters.service_quotation_id,
    short_description: loaderDeps.filters.short_description,
    status_name: loaderDeps.filters.status_name,
  } as unknown as ListServiceNegotiationsParams;

  const { data, isLoading, isError } = useListServiceNegotiations(listParams);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("serviceNegotiation.fetchError"));
    }
  }, [isError, t]);

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: value,
        }),
      });
    },
    [navigate],
  );

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("serviceNegotiation.title")}
    >
      <AppCard>
        <ServiceNegotiationsFilters loading={isLoading} />

        <TabContext value={selectedTab as string}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={selectedTab}>
              <RouteTabLink
                from="/vbuyer/service_negotiations/"
                label={t("quotation.tabs.all")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "all",
                })}
                to="/vbuyer/service_negotiations"
                value="all"
              />
              <RouteTabLink
                from="/vbuyer/service_negotiations/"
                label={t("quotation.tabs.open")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "open",
                })}
                to="/vbuyer/service_negotiations"
                value="open"
              />
              <RouteTabLink
                from="/vbuyer/service_negotiations/"
                label={t("quotation.tabs.ongoing")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "ongoing",
                })}
                to="/vbuyer/service_negotiations"
                value="ongoing"
              />
              <RouteTabLink
                from="/vbuyer/service_negotiations/"
                label={t("quotation.tabs.completed")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "completed",
                })}
                to="/vbuyer/service_negotiations"
                value="completed"
              />
            </Tabs>
          </Box>
        </TabContext>

        <ListServiceNegotiations
          loading={isLoading}
          serviceNegotiations={data?.results || []}
        />

        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
          <Pagination
            color="primary"
            count={Math.ceil(
              (data?.total_items || 0) / (rowsPerPage as number),
            )}
            onChange={handlePageChange}
            page={page as unknown as number}
          />
        </Box>
      </AppCard>
    </PageContainer>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/vbuyer/service_negotiations/")({
  validateSearch: z.object({
    created_at: z.optional(z.string()).catch(undefined),
    identifier: z.optional(z.string()).catch(undefined),
    page: z.optional(z.coerce.number()).catch(undefined),
    reference_number: z.optional(z.string()).catch(undefined),
    rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
    service_quotation_id: z.optional(z.coerce.number()).catch(undefined),
    short_description: z.optional(z.string()).catch(undefined),
    status_name: z
      .optional(z.union([z.array(z.string()), z.string()]))
      .catch(undefined),
    tab: z.optional(z.string()).catch("all"),
  }),
  loaderDeps: ({ search }: { search: NegotiationsSearch }) => {
    const selectedStatusNames = parseSearchStrings(search.status_name);
    const effectiveTab = search.tab ?? "all";

    let statusFilter: string[] | undefined;
    if (effectiveTab === "open") {
      statusFilter = ["Waiting Offer"];
    } else if (effectiveTab === "ongoing") {
      statusFilter = [
        "Offer Made",
        "Pending Buyer",
        "Possible Winner",
        "Waiting For Renegotiation",
        "Starting Evaluation Proposals",
        "Waiting Bid Timeout",
        "Waiting Bid",
        "Evaluating Proposals",
        "Issuing Purchase Order",
        "Purchase Order Created",
        "No Offers",
        "Inconclusive Evaluation",
        "Reject Reverted",
        "Rejected",
      ];
    } else if (["completed", "finished"].includes(effectiveTab)) {
      statusFilter = [...SERVICE_NEGOTIATION_COMPLETED_STATUS_NAMES];
    }

    let finalStatusNames: string[] | undefined;
    if (effectiveTab === "all") {
      finalStatusNames = selectedStatusNames;
    } else if (selectedStatusNames && selectedStatusNames.length > 0) {
      const intersection = statusFilter?.filter((s) =>
        selectedStatusNames.includes(s),
      );
      finalStatusNames = intersection?.length ? intersection : statusFilter;
    } else {
      finalStatusNames = statusFilter;
    }

    return {
      filters: {
        created_at: search.created_at || undefined,
        identifier: search.identifier || undefined,
        reference_number: search.reference_number,
        service_quotation_id: search.service_quotation_id
          ? Number(search.service_quotation_id)
          : undefined,
        short_description: search.short_description || undefined,
        status_name: finalStatusNames,
      },
      items_per_page: search.rowsPerPage || 25,
      page: search.page || 1,
    };
  },
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Service Negotiation - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ServiceNegotiations,
});
